// Angular
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from "@angular/router";
import { map, pluck, tap } from "rxjs/operators";
import { IQuotationsQuotation } from "libraries/service/resources/quotation-quotations/IQuotationsQuotation";
import { Yii } from "libraries/interfaces/Yii";
import { NotificationType } from "libraries/interfaces/INotification";
import { LabelService } from "libraries/service/label/label.service";
import { InstanceService } from "libraries/service/instance/instance.service";
import { CurrencyListResourceService } from "libraries/service/resources/currency-list/currency-list-resource.service";
import { QuotationBreadcrumbService } from "../../shared/components/breadcrumb/quotation-breadcrumb.service";
import { IBreadcrumbItem } from "libraries/service/breadcrumb/IBreadcrumbItem";
import { AppHeaderTitleQuotationService } from 'app_quotation/shared/services/app-header-title-quotation/app-header-title-quotation.service';
import { ErrorTracingService, TraceSeverity } from "libraries/service/error-tracing/error-tracing.service";
import { CostAppConfigService } from './services/cost-app-config/cost-app-config.service';
import { CachedQuotationService } from 'app_quotation/shared/services/cached-quotation.service';
import { ITab, TabCode } from 'app/shared/model/ITab';
import { AppRootService } from 'app/shared/services/app-root/app-root-service';
import { EHosts } from "libraries/enums/EHosts";
import { IQuotationResolved } from 'app_quotation/shared/services/quotation-resolve/quotation.resolve';
import { IXRateHandler, XRateService } from 'app_quotation/shared/services/xRate/xRate.service';
import { WatabiUrlService } from './services/watabi-url/watabi-url.service';
import { ILabels } from "../../../libraries/interfaces/ILabels";
import { BtnStyleTypes } from "../../../libraries/ui_button/btn.component";
import { EQuotationEntryPoints } from "libraries/enums/e-quotation-entry-points";

export interface INotification {
    active: boolean;
    message: string;
}

@Component({
    selector: 'cost',
    template: `
        <grid-tab-navigation *ngIf="tabs.length !== 0"
                             [tabs]="tabs"
                             [deactivationReason]="deactivationReason"
                             [canValidate]="false"
                             (onClose)="redirect()"></grid-tab-navigation>

        <div class="app-wrapper app-quotation">
            <loader *ngIf="loading.active; else loadedContainer" [message]="loading.message"></loader>
            <ng-template #loadedContainer>
                <exchange-rate-information [quotation]="quotationForExchangeRate"
                                           (refreshExchangeRateEvent)="refreshXRates()">
                </exchange-rate-information>
                <!--<rate-invalidity-notification *ngIf="quotation"
                                              [isXRateValid]="quotation?.isXRateValid"
                                              [hasFoundValidRate]="hasFoundRate"
                                              (onRefreshRates)="refreshXRates()">
                </rate-invalidity-notification>-->
                <notification *ngIf="errorNotification.active"
                              [type]="notificationType.Error">
                        {{errorNotification.message}}
                        <ng-container *ngIf="showRefreshButton">
                            <btn [value]="wording.refresh" (onClick)="refresh()" [type]="refreshButtonType"></btn>
                        </ng-container>
                </notification>
                <cost-warning *ngIf="quotation?.warnings"
                              [warningsList]="quotation.warnings">
                </cost-warning>
                <cost-grid *ngIf="quotation"
                           [quotation]="quotation"
                           (isXRateNoValid)="isXRateNoValid(false)"
                           (quotationChange)="quotationForExchangeRate=$event"
                           (onError)="showError($event)">
                </cost-grid>
            </ng-template>
        </div>
    `,
    styles: [`
        .app-quotation {
            width: auto;
            max-width: unset;
            margin: .75rem;
        }
        
    `]
})
export class CostComponent implements OnInit, IXRateHandler {
    public errorNotification: INotification = {
        active: false,
        message: Yii.t('wui-quotation-js', 'Erreur lors du chargement de la Cotation')
    };

    public showRefreshButton: boolean;
    public wording: ILabels = {
        refresh : Yii.t('wui-shared-js', 'Rafraîchir la page')
    };
    public refreshButtonType: BtnStyleTypes = BtnStyleTypes.actionLink;

    public notificationType: typeof NotificationType = NotificationType;
    public quotationEntryPoints: typeof EQuotationEntryPoints = EQuotationEntryPoints;

    public loading: INotification = {
        active: false,
        message: Yii.t('wui-quotation-js', 'Chargement de la Cotation en cours'),
    };

    public quotation: IQuotationsQuotation;
    public quotationForExchangeRate: IQuotationsQuotation;
    public hasFoundRate: boolean = true;
    public showCloseButton: boolean = false;
    public breadcrumbItems: IBreadcrumbItem[];
    public tabs: ITab[] = [];
    public deactivationReason: string = Yii.t('wui-quotation-js', 'Aucun bloc en option trouvé');

    constructor(
        private readonly activatedRoute: ActivatedRoute,
        private readonly cachedQuotationService: CachedQuotationService,
        private readonly labelService: LabelService,
        private readonly instanceService: InstanceService,
        private readonly currencyListResource: CurrencyListResourceService,
        private readonly quotationBreadcrumbService: QuotationBreadcrumbService,
        private readonly appHeaderTitleQuotationService: AppHeaderTitleQuotationService,
        private readonly costAppConfigService: CostAppConfigService,
        private readonly errorTracingService: ErrorTracingService,
        private readonly appRootService: AppRootService,
        private readonly xRateService: XRateService,
        private readonly watabiUrlService: WatabiUrlService,
    ) {
    }

    public ngOnInit(): void {
        this.breadcrumbItems = this.quotationBreadcrumbService.activateItem(0);

        this.activatedRoute.data.pipe(
            pluck('quotation'),
            tap(() => {
                this.currencyListResource.getCurrencyOption("EUR");
                this.cachedQuotationService.get(+this.activatedRoute.snapshot.paramMap.get('id'));
            }),
            map(({hasOptions, quotation}: IQuotationResolved) => {
                this.loading.active = false;
                this.initQuotation(quotation);

                this.appHeaderTitleQuotationService.changeTitle(quotation.id);

                if (quotation.externalReference
                    && quotation.externalReference.host
                    && quotation.externalReference.host.toLowerCase() === EHosts.WATABI.toLowerCase()) {
                    this.showCloseButton = true;
                }

                this.costAppConfigService.prepareConfig(quotation);

                return hasOptions;
            }),
        ).subscribe({
            next: (hasOptions: boolean): void => {
                if (this.quotation.title) {
                    this.appRootService.setAppConfig(
                        {
                            ...this.appRootService.getAppConfig(this.appRootService.getCurrentAppFromLocation()),
                            secondaryName: this.quotation.title
                        }
                    );
                    this.appRootService.setBrowserTitle();
                }

                const parentTab: ITab = this.appRootService.currentAppConfig.tabs.find((tab: ITab) => tab.code === EQuotationEntryPoints.cost);

                if (parentTab) {
                    this.tabs = parentTab.tabs.map((tab: ITab) => {
                        if (!hasOptions && tab.code === TabCode.option) {
                            return {
                                ...tab,
                                disabled: true
                            };
                        }

                        return tab;
                    });
                }
            },
            error: (error: Error): void => {
                //manage Error
                this.loading.active = false;
                this.errorNotification = {
                    active: true,
                    message: this.labelService.completeLabel(
                        Yii.t('wui-quotation-js', 'Erreur lors du chargement de la Cotation : %placeholder%'),
                        error.message,
                    ),
                };
                this.errorTracingService.captureMessage(error.message, TraceSeverity.Warning);
                // Otherwise, it's not possible to see the stack trace
                console.warn(error);
            }
        });
    }

    public isXRateNoValid(isXRateValid: boolean) {
        this.quotation.isXRateValid = isXRateValid;
    }

    public redirect(): void {
        window.location.href = this.watabiUrlService.generateFrontendQuoteUrl(this.quotation);
    }

    public initQuotation(quotation: IQuotationsQuotation): void {
        this.quotation = quotation;
        this.quotationForExchangeRate = quotation;
    }

    public refreshXRates(): void {
        this.xRateService.refreshXRatesAndComponentState(this);
    }

    public showError(errorMessage: string): void {
        this.showRefreshButton = true;
        this.errorNotification = {
            active: true,
            message: errorMessage,
        };
    }

    public refresh(): void {
        document.location.reload();
    }
}
